/**
 * Audit log - dense operational table answering who changed what, when, and
 * whether it succeeded.
 *
 * Wires `GET /api/v1/admin/audit` (action, resourceType, actorEmail, from/to,
 * limit/offset). Result is filtered client-side on the current page.
 *
 * Columns: Time · Actor · Action · Resource · Result.
 * From/To + clear sit above the table; column filters are the header controls.
 *
 * Row activate opens a read-only detail view from the list item in memory.
 */
import { el, mount } from "../utils/dom";
import { renderPageHeader } from "../components/pageHeader";
import { renderLoadingState, renderErrorState, renderEmptyState, renderUnavailableState } from "../components/states";
import { renderStatusText } from "../components/statusText";
import { renderAbsoluteTime } from "../components/dateTime";
import { EMPTY, formatNumber, resourceTypeLabel, actionLabel } from "../utils/format";
import { openModal } from "../components/modal";
import { listAudit } from "../services/audit";
import { ApiError } from "../services/apiClient";
import type { AuditChangeEntry, AuditItem } from "../types/api";

const LIMIT_OPTIONS = [25, 50, 100, 200];
const RESOURCE_TYPES = ["service_account", "auth_group", "sync_settings", "admin_user", "sync"];

interface Filters {
  action: string;
  resourceType: string;
  actorEmail: string;
  result: "" | "success" | "failed";
  from: string;
  to: string;
}

export class AuditLogPage {
  private body: HTMLElement = el("div", {}, [renderLoadingState("Loading audit trail…")]);
  private paginationEl: HTMLElement = el("div", {});
  private actionSelect!: HTMLSelectElement;
  private resourceTypeSelect!: HTMLSelectElement;
  private actorInput!: HTMLInputElement;
  private resultSelect!: HTMLSelectElement;
  private fromInput!: HTMLInputElement;
  private toInput!: HTMLInputElement;
  private filters: Filters = {
    action: "",
    resourceType: "",
    actorEmail: "",
    result: "",
    from: "",
    to: "",
  };
  private limit = 50;
  private offset = 0;
  private total = 0;
  private knownActions: string[] = [];

  mount(outlet: HTMLElement, navigate: (path: string) => void): void {
    this.actionSelect = headerSelect("Filter by action", [el("option", { value: "" }, ["Action"])], (value) => {
      this.filters.action = value;
      this.offset = 0;
      void this.load();
    });

    this.resourceTypeSelect = headerSelect(
      "Filter by resource type",
      [
        el("option", { value: "" }, ["Resource"]),
        ...RESOURCE_TYPES.map((rt) => el("option", { value: rt }, [resourceTypeLabel(rt)])),
      ],
      (value) => {
        this.filters.resourceType = value;
        this.offset = 0;
        void this.load();
      }
    );

    this.actorInput = el("input", {
      className: "input input--header",
      type: "search",
      placeholder: "Actor",
      "aria-label": "Filter by actor email",
      onclick: stopRow,
      onkeydown: (ev: KeyboardEvent) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          this.applyActorFilter();
        }
      },
      onchange: () => this.applyActorFilter(),
      oninput: () => syncHeaderActive(this.actorInput, this.actorInput.value.trim() !== ""),
    }) as HTMLInputElement;

    this.resultSelect = headerSelect(
      "Filter by result",
      [
        el("option", { value: "" }, ["Result"]),
        el("option", { value: "success" }, ["Success"]),
        el("option", { value: "failed" }, ["Failed"]),
      ],
      (value) => {
        this.filters.result = value as Filters["result"];
        this.offset = 0;
        void this.load();
      }
    );

    this.fromInput = el("input", {
      className: "input input--sm input--date",
      type: "date",
      title: "From (inclusive)",
      "aria-label": "From date",
      onchange: (ev: Event) => {
        this.filters.from = toIsoStart((ev.target as HTMLInputElement).value);
        this.offset = 0;
        void this.load();
      },
    }) as HTMLInputElement;

    this.toInput = el("input", {
      className: "input input--sm input--date",
      type: "date",
      title: "To (inclusive)",
      "aria-label": "To date",
      onchange: (ev: Event) => {
        this.filters.to = toIsoEnd((ev.target as HTMLInputElement).value);
        this.offset = 0;
        void this.load();
      },
    }) as HTMLInputElement;

    const clearButton = el(
      "button",
      {
        className: "btn-link",
        type: "button",
        onclick: () => this.clearFilters(),
      },
      ["Clear filters"]
    );

    const rangeToolbar = el("div", { className: "toolbar toolbar--range" }, [
      el("div", { className: "range-filter" }, [
        el("span", { className: "range-filter__label" }, ["From"]),
        this.fromInput,
        el("span", { className: "range-filter__sep" }, ["to"]),
        this.toInput,
      ]),
      clearButton,
    ]);

    const refreshButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        "aria-label": "Refresh audit trail",
        onclick: () => void this.load(),
      },
      ["Refresh"]
    );

    mount(
      outlet,
      el("div", {}, [
        renderPageHeader({
          title: "Audit Trail",
          subtitle: "Admin changes: who, what, when.",
          back: { fallbackPath: "/dashboard", navigate, preferHistory: false, label: "Back to Dashboard" },
          actions: [refreshButton],
        }),
        el("div", { className: "panel panel--standard" }, [rangeToolbar, this.body, this.paginationEl]),
      ])
    );

    void this.load();
  }

  destroy(): void {
    // No interval - audit entries only appear when an admin mutation
    // happens, which won't occur while an operator is just reading this page.
  }

  private applyActorFilter(): void {
    const next = this.actorInput.value.trim();
    syncHeaderActive(this.actorInput, next !== "");
    if (next === this.filters.actorEmail) return;
    this.filters.actorEmail = next;
    this.offset = 0;
    void this.load();
  }

  private clearFilters(): void {
    this.filters = {
      action: "",
      resourceType: "",
      actorEmail: "",
      result: "",
      from: "",
      to: "",
    };
    this.actionSelect.value = "";
    this.resourceTypeSelect.value = "";
    this.actorInput.value = "";
    this.resultSelect.value = "";
    this.fromInput.value = "";
    this.toInput.value = "";
    syncHeaderActive(this.actionSelect, false);
    syncHeaderActive(this.resourceTypeSelect, false);
    syncHeaderActive(this.actorInput, false);
    syncHeaderActive(this.resultSelect, false);
    this.offset = 0;
    void this.load();
  }

  private async load(): Promise<void> {
    mount(this.body, renderLoadingState("Loading audit trail…"));
    mount(this.paginationEl, null);

    try {
      const data = await listAudit({
        action: this.filters.action || undefined,
        resourceType: this.filters.resourceType || undefined,
        actorEmail: this.filters.actorEmail || undefined,
        from: this.filters.from || undefined,
        to: this.filters.to || undefined,
        limit: this.limit,
        offset: this.offset,
      });

      this.total = data.pagination.total;
      this.syncActionOptions(data.actions);

      const items = applyResultFilter(data.items, this.filters.result);

      if (!items.length) {
        mount(
          this.body,
          el("div", {}, [
            this.renderTable([]),
            renderEmptyState(
              this.offset > 0 ? "No more audit entries." : "No entries match these filters."
            ),
          ])
        );
      } else {
        mount(this.body, this.renderTable(items));
      }

      mount(this.paginationEl, this.renderPagination(data.items.length));
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 503)) {
        mount(
          this.body,
          renderUnavailableState(err.message, "Audit history could not be loaded.")
        );
      } else {
        mount(this.body, renderErrorState(errorMessage(err), { onRetry: () => void this.load() }));
      }
    }
  }

  private syncActionOptions(actions: string[]): void {
    const merged = Array.from(new Set([...this.knownActions, ...actions])).sort();
    if (merged.length === this.knownActions.length && this.knownActions.length > 0) {
      return;
    }
    this.knownActions = merged;

    const current = this.actionSelect.value;
    this.actionSelect.replaceChildren(
      el("option", { value: "" }, ["Action"]),
      ...merged.map((a) => el("option", { value: a }, [actionLabel(a)]))
    );
    this.actionSelect.value = current;
    syncHeaderActive(this.actionSelect, current !== "");
  }

  private renderTable(items: AuditItem[]): HTMLElement {
    const table = el("table", { className: "data-table" }, [
      el("thead", {}, [
        el("tr", {}, [
          el("th", { scope: "col" }, ["Time"]),
          filterHeader(this.actorInput),
          filterHeader(this.actionSelect),
          filterHeader(this.resourceTypeSelect),
          filterHeader(this.resultSelect),
        ]),
      ]),
      el(
        "tbody",
        {},
        items.map((item) => {
          const failed = isFailedResult(item.statusCode);
          return el(
            "tr",
            {
              className: ["data-table__row--nav", failed ? "flag-danger" : ""].filter(Boolean).join(" "),
              tabIndex: 0,
              role: "button",
              "aria-label": `Audit ${item.id}: ${actionLabel(item.action)}`,
              onclick: () => this.openDetail(item),
              onkeydown: (ev: KeyboardEvent) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  this.openDetail(item);
                }
              },
            },
            [
              el("td", {}, [renderAbsoluteTime(item.occurredAt)]),
              el("td", {}, [actorLabel(item)]),
              el("td", {}, [actionLabel(item.action)]),
              el("td", {}, [renderResource(item)]),
              el("td", {}, [resultStatus(item.statusCode)]),
            ]
          );
        })
      ),
    ]);

    return el("div", { className: "data-table-wrap data-table-wrap--fit" }, [table]);
  }

  private renderPagination(pageCount: number): HTMLElement {
    const start = this.total === 0 ? 0 : this.offset + 1;
    const end = this.offset + pageCount;

    const limitSelect = el(
      "select",
      {
        className: "input input--sm",
        "aria-label": "Rows per page",
        onchange: (ev: Event) => {
          this.limit = Number((ev.target as HTMLSelectElement).value);
          this.offset = 0;
          void this.load();
        },
      },
      LIMIT_OPTIONS.map((n) => el("option", { value: String(n), selected: n === this.limit }, [`${n} / page`]))
    );

    const prevButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        disabled: this.offset === 0,
        "aria-label": "Previous page",
        onclick: () => {
          this.offset = Math.max(0, this.offset - this.limit);
          void this.load();
        },
      },
      ["Prev"]
    );

    const nextButton = el(
      "button",
      {
        className: "btn btn-ghost btn-sm",
        type: "button",
        disabled: end >= this.total,
        "aria-label": "Next page",
        onclick: () => {
          this.offset = this.offset + this.limit;
          void this.load();
        },
      },
      ["Next"]
    );

    return el("div", { className: "pagination-bar" }, [
      el("span", { className: "next-cron__empty" }, [
        `Showing ${formatNumber(start)}-${formatNumber(end)} of ${formatNumber(this.total)}`,
      ]),
      el("div", { className: "row row--wrap" }, [limitSelect, prevButton, nextButton]),
    ]);
  }

  private openDetail(item: AuditItem): void {
    openModal(renderDetail(item), { title: `Audit #${item.id}` });
  }
}

function stopRow(ev: Event): void {
  ev.stopPropagation();
}

function headerSelect(
  ariaLabel: string,
  options: HTMLElement[],
  onChange: (value: string) => void
): HTMLSelectElement {
  const select = el(
    "select",
    {
      className: "input input--header",
      "aria-label": ariaLabel,
      onclick: stopRow,
      onchange: (ev: Event) => {
        const value = (ev.target as HTMLSelectElement).value;
        syncHeaderActive(select, value !== "");
        onChange(value);
      },
    },
    options
  ) as HTMLSelectElement;
  return select;
}

function syncHeaderActive(control: HTMLElement, active: boolean): void {
  control.classList.toggle("input--header-active", active);
}

function filterHeader(control: HTMLElement): HTMLElement {
  return el("th", { scope: "col", className: "data-table__th-filter" }, [control]);
}

function applyResultFilter(items: AuditItem[], result: Filters["result"]): AuditItem[] {
  if (result === "success") return items.filter((item) => !isFailedResult(item.statusCode));
  if (result === "failed") return items.filter((item) => isFailedResult(item.statusCode));
  return items;
}

function renderDetail(item: AuditItem): HTMLElement {
  const rows: HTMLElement[] = [
    detailRow("Time", renderAbsoluteTime(item.occurredAt)),
    detailRow("Actor", el("span", {}, [actorLabel(item)])),
    detailRow("Action", el("span", {}, [actionLabel(item.action)])),
    detailRow("Resource", renderResource(item)),
    detailRow("Resource id", el("span", { className: "mono" }, [item.resourceId ?? EMPTY])),
    detailRow("Result", resultStatus(item.statusCode)),
    detailRow(
      "Request",
      el("span", { className: "mono" }, [
        [item.requestMethod, item.requestPath].filter(Boolean).join(" ") || EMPTY,
      ])
    ),
  ];

  const body: HTMLElement[] = [el("div", { className: "next-cron" }, rows)];

  const changeEntries = item.changes ? Object.entries(item.changes) : [];
  if (changeEntries.length) {
    body.push(
      el("p", { className: "panel__title mt-8" }, ["Changes"]),
      renderChangesTable(item.changes as Record<string, AuditChangeEntry>)
    );
  }

  return el("div", {}, body);
}

function renderChangesTable(changes: Record<string, AuditChangeEntry>): HTMLElement {
  const table = el("table", { className: "data-table" }, [
    el("thead", {}, [el("tr", {}, [el("th", {}, ["Field"]), el("th", {}, ["Before"]), el("th", {}, ["After"])])]),
    el(
      "tbody",
      {},
      Object.entries(changes).map(([field, change]) =>
        el("tr", {}, [
          el("td", { className: "mono" }, [field]),
          el("td", { className: "mono data-table__cell-muted" }, [stringifyValue(change.before)]),
          el("td", { className: "mono" }, [stringifyValue(change.after)]),
        ])
      )
    ),
  ]);
  return el("div", { className: "data-table-wrap" }, [table]);
}

function detailRow(label: string, value: HTMLElement): HTMLElement {
  return el("div", { className: "next-cron__row" }, [
    el("span", { className: "next-cron__label" }, [label]),
    value,
  ]);
}

/**
 * Actor - exact backend identifier only. Prefer the most specific non-null
 * field the API returned; never invent names, emails, avatars, or roles.
 */
function actorLabel(item: AuditItem): string {
  return item.actorDisplayName || item.actorEmail || item.actorId || item.actorType;
}

/** Single Resource cell: type - name, using only backend-provided values. */
function renderResource(item: AuditItem): HTMLElement {
  const type = resourceTypeLabel(item.resourceType);
  const name = item.resourceName?.trim();
  if (name) {
    return el("span", {}, [
      el("span", {}, [name]),
      el("span", { className: "data-table__cell-muted" }, [` · ${type}`]),
    ]);
  }
  return el("span", {}, [type]);
}

function isFailedResult(statusCode: number | null): boolean {
  // 2xx and 3xx are successful outcomes (Entra login audits a 302 redirect).
  return statusCode !== null && statusCode >= 400;
}

function resultStatus(statusCode: number | null): HTMLElement {
  if (statusCode === null) return el("span", {}, [EMPTY]);
  if (!isFailedResult(statusCode)) {
    return renderStatusText("Success", "success");
  }
  return renderStatusText(`Failed (${statusCode})`, "danger");
}

function stringifyValue(value: unknown): string {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function toIsoStart(dateOnly: string): string {
  return dateOnly ? `${dateOnly}T00:00:00Z` : "";
}

function toIsoEnd(dateOnly: string): string {
  return dateOnly ? `${dateOnly}T23:59:59Z` : "";
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Something went wrong loading this data.";
}
